import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaArrowRight, FaCopy, FaRegCheckCircle, FaCloud, 
  FaPlay, FaCheck, FaLink, FaDatabase, FaCreditCard, 
  FaChartLine, FaSlidersH, FaServer, FaCogs, FaUsers, 
  FaExclamationTriangle, FaBell, FaSync
} from 'react-icons/fa';
import { useTheme } from '../ThemeContext';

export default function SaaSDetail() {
  useTheme();

  // 1. Interactive Workspace Ops Builder Settings
  const [selectedTenant, setSelectedTenant] = useState('gymscribe'); // gymscribe, bakeshop, enterprisetech
  const [pricingPlan, setPricingPlan] = useState('growth'); // dev, growth, enterprise
  const [copiedSpec, setCopiedSpec] = useState(false);

  // Integration switches
  const [stripeSync, setStripeSync] = useState(true);
  const [sendgridSync, setSendgridSync] = useState(false);
  const [slackSync, setSlackSync] = useState(true);
  const [vercelSync, setVercelSync] = useState(false);

  // Webhook audit log feed
  const [webhookLogs, setWebhookLogs] = useState([
    'SYSTEM: Workspace ready. Listening for pipeline triggers...'
  ]);

  // Handle updates to webhook logs
  const triggerLog = (message) => {
    setWebhookLogs(prev => [message, ...prev.slice(0, 7)]);
  };

  // Live fluctuating telemetry values based on tenant
  const [activeUsersCount, setActiveUsersCount] = useState(482);
  const [incomingRequests, setIncomingRequests] = useState(140);
  const [mrrValue, setMrrValue] = useState(8240);

  // Dynamic values adjustment based on tenant/plan
  useEffect(() => {
    let baseUsers = 120;
    let baseRequests = 85;
    let baseMrr = 0;

    if (selectedTenant === 'gymscribe') {
      baseUsers = 450;
      baseRequests = 220;
      baseMrr = 1850;
    } else if (selectedTenant === 'bakeshop') {
      baseUsers = 780;
      baseRequests = 490;
      baseMrr = 3450;
    } else if (selectedTenant === 'enterprisetech') {
      baseUsers = 4250;
      baseRequests = 1890;
      baseMrr = 17500;
    }

    if (pricingPlan === 'dev') {
      setActiveUsersCount(Math.min(baseUsers, 150));
      setIncomingRequests(Math.min(baseRequests, 90));
      setMrrValue(0);
    } else if (pricingPlan === 'growth') {
      setActiveUsersCount(baseUsers);
      setIncomingRequests(baseRequests);
      setMrrValue(baseMrr);
    } else if (pricingPlan === 'enterprise') {
      setActiveUsersCount(baseUsers * 2);
      setIncomingRequests(baseRequests * 3);
      setMrrValue(baseMrr * 4);
    }

    triggerLog(`SYSTEM: Switched configuration payload to Tenant [${selectedTenant.toUpperCase()}] on [${pricingPlan.toUpperCase()} PLAN]`);
  }, [selectedTenant, pricingPlan]);

  // Webhook toggle sideffect logs
  useEffect(() => {
    if (stripeSync) triggerLog('[hook.stripe] subscription.created ➜ active ($' + (mrrValue / 10).toFixed(0) + '/mo)');
  }, [stripeSync, mrrValue]);

  useEffect(() => {
    if (sendgridSync) triggerLog('[mail.sendgrid] dispatching activation template ➜ user_verified');
  }, [sendgridSync]);

  useEffect(() => {
    if (slackSync) triggerLog('[api.slack] notify ➜ channel #sales-alerts: "New signup unlocked!"');
  }, [slackSync]);

  useEffect(() => {
    if (vercelSync) triggerLog('[deploy.vercel] pipeline compilation triggered ➜ deployment ready');
  }, [vercelSync]);

  // Live requests noise fluctuation ticker
  useEffect(() => {
    const timer = setInterval(() => {
      setIncomingRequests(prev => {
        const delta = Math.floor(Math.random() * 9) - 4;
        return Math.max(10, prev + delta);
      });
    }, 1200);
    return () => clearInterval(timer);
  }, []);

  // 2. Simulated Developer Journey CLI timeline
  // Stages: 'audit' | 'routing' | 'billing' | 'loadtesting' | 'delivery'
  const [pipelineStage, setPipelineStage] = useState('audit');
  
  // CLI typing simulator sub-states
  const [compilingCode, setCompilingCode] = useState(false);
  const [compiledSuccess, setCompiledSuccess] = useState(false);
  const [terminalLogs, setTerminalLogs] = useState('');

  const handleRunPipeline = () => {
    if (compilingCode) return;
    setCompilingCode(true);
    setCompiledSuccess(false);
    setTerminalLogs('$ npm run build:saas-infrastructure\n');

    setTimeout(() => {
      setTerminalLogs(prev => prev + '> Audit: Parsing multi-tenant isolated schemas... OK\n');
    }, 550);

    setTimeout(() => {
      setTerminalLogs(prev => prev + '> Routing: Mapping tenant HTTP header subdomains... OK\n');
    }, 1100);

    setTimeout(() => {
      setTerminalLogs(prev => prev + '> Billing: Linking Stripe webhook middleware endpoints... OK\n');
    }, 1650);

    setTimeout(() => {
      setTerminalLogs(prev => prev + '> Safety: Running test suite for tenant isolation... 100% SECURE\n');
    }, 2200);

    setTimeout(() => {
      setTerminalLogs(prev => prev + '✔ Cloud deployment status: ACTIVE. Domain mapped successfully.');
      setCompilingCode(false);
      setCompiledSuccess(true);
    }, 2800);
  };

  const resetPipeline = () => {
    setCompiledSuccess(false);
    setTerminalLogs('');
  };

  const handleCopySpec = () => {
    const specSummary = `BCC SaaS Workspace Blueprint spec:
- Managed Tenant: ${selectedTenant.toLowerCase()}
- Billing Plan: ${pricingPlan.toUpperCase()}
- Integrations Configured: ${[
  stripeSync ? 'Stripe Checkout' : '',
  sendgridSync ? 'SendGrid System Mails' : '',
  slackSync ? 'Slack Active Notifications' : '',
  vercelSync ? 'Vercel Autodeployments' : ''
].filter(Boolean).join(', ') || 'None'}`;
    navigator.clipboard.writeText(specSummary);
    setCopiedSpec(true);
  };

  useEffect(() => {
    if (copiedSpec) {
      const timer = setTimeout(() => setCopiedSpec(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedSpec]);

  return (
    <div className="min-h-screen overflow-x-hidden transition-all duration-300" style={{ background: 'var(--bg-primary)' }}>
      
      {/* ── HERO SECTION ── */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden animate-fade-in" 
               style={{ 
                 background: 'var(--bg-card)',
                 borderBottom: '1px solid var(--section-divider)' 
               }}>
        <div className="absolute top-0 right-0 w-[550px] h-[550px] rounded-full blur-[140px] opacity-10 pointer-events-none bg-emerald-500" />
        
        <div className="section-container relative z-10 text-center max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6"
               style={{ 
                 background: 'var(--accent-soft)',
                 border: '1px solid var(--border-color)' 
               }}>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-500">Core Services</span>
          </div>

          <h1 className="display-lg mb-6 leading-tight font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>
            High-Performance <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-teal-400">SaaS Platforms</span>.
          </h1>
          
          <p className="text-lg leading-relaxed mb-10 max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            We design, engineer, and deploy scalable multi-tenant SaaS structures. Complete with Stripe subscription matrices, customized telemetry dashboards, and isolated relational database schemas.
          </p>

          <div className="flex justify-center gap-4">
            <Link to="/contactus" className="btn-primary inline-flex bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-none hover:opacity-90">
              Build Your SaaS Spec <FaArrowRight size={11} className="ml-1.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── CAPABILITIES DECK (WHAT WE ENGINEER) ── */}
      <section className="py-24 px-6 border-b animate-fade-in" style={{ background: 'var(--bg-primary)', borderColor: 'var(--section-divider)' }}>
        <div className="section-container max-w-6xl">
          <div className="text-center mb-20">
            <p className="eyebrow mb-2">Our Capabilities</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>What We Build</h2>
            <p className="subheadline max-w-2xl mx-auto">
              From multi-tenant isolation layers to automated subscription grids — we develop full-stack SaaS pipelines ready for immediate use.
            </p>
          </div>

          <div className="space-y-28">

            {/* Row 1: Multi-Tenancy */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="p-8 rounded-3xl border bg-gradient-to-br from-emerald-500/10 to-teal-500/5 shadow-inner" style={{ borderColor: 'var(--border-color)' }}>
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 mb-6">
                  <FaDatabase size={20} />
                </div>
                <h3 className="text-lg font-black mb-3 text-white">Advanced Database Tenant Isolation</h3>
                <p className="text-sm leading-relaxed text-zinc-400">
                  Ensure strict security and data compartmentalization. We design custom connection rules and table structures separating individual tenant workspaces while keeping shared app logic lightweight.
                </p>
              </div>
              <div>
                <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-emerald-400 mb-4">
                  <span className="w-4 h-px bg-emerald-400 inline-block" /> Architecture
                </span>
                <h3 className="text-3xl font-black mb-4 leading-tight" style={{ color: 'var(--text-primary)' }}>
                  Secure data separation at scale
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                  We prioritize workspace safety using separate database fields, tokens segregation, and secure routing pipelines so corporate databases are fully protected against concurrent multi-user overhead.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <FaCheck size={10} className="text-emerald-500 mt-1 shrink-0" />
                    Separate subdomains & URL routing protocols per tenant.
                  </li>
                  <li className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <FaCheck size={10} className="text-emerald-500 mt-1 shrink-0" />
                    Strict row-level security policy configurations in PostgreSQL.
                  </li>
                </ul>
              </div>
            </div>

            {/* Row 2: Billing & Subscriptions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-teal-400 mb-4">
                  <span className="w-4 h-px bg-teal-400 inline-block" /> Billing Core
                </span>
                <h3 className="text-3xl font-black mb-4 leading-tight" style={{ color: 'var(--text-primary)' }}>
                  Flexible Stripe Subscription Grids
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Simplify revenue mapping with Stripe subscription integrations, invoicing logs, trial configurations, and automatic client upgrades mapped to platform usage.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <FaCheck size={10} className="text-teal-500 mt-1 shrink-0" />
                    Tiered subscriber matrix (Monthly/Annual billing plans).
                  </li>
                  <li className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <FaCheck size={10} className="text-teal-500 mt-1 shrink-0" />
                    Stripe Webhook middlewares listening for lifecycle metrics.
                  </li>
                </ul>
              </div>
              <div className="order-1 lg:order-2 p-8 rounded-3xl border bg-gradient-to-br from-teal-500/10 to-emerald-500/5 shadow-inner" style={{ borderColor: 'var(--border-color)' }}>
                <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center text-teal-400 mb-6">
                  <FaCreditCard size={20} />
                </div>
                <h3 className="text-lg font-black mb-3 text-white">Stripe Custom Paywalls</h3>
                <p className="text-sm leading-relaxed text-zinc-400">
                  Allow clients to buy packages directly. Built with localized tax rules, seat-based dynamic add-ons, and secure client-facing billing configuration panels.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── THE SAAS BLUEPRINT BUILDER ── */}
      <section className="py-24 px-6 border-b" style={{ background: 'var(--bg-surface)', borderColor: 'var(--section-divider)' }}>
        <div className="section-container max-w-5xl">
          <div className="text-center mb-16">
            <p className="eyebrow mb-2">Live Workspace Simulator</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>SaaS Cockpit Blueprint</h2>
            <p className="subheadline max-w-2xl mx-auto">
              Simulate multi-tenancy workspaces, choose subscription plans, and toggle webhook channels to observe telemetry logs in real-time.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            
            {/* Left Controls (5 columns) */}
            <div className="lg:col-span-5 flex flex-col justify-between border rounded-3xl p-6 shadow-xl"
                 style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
              <div>
                <h3 className="text-xs font-black uppercase tracking-wider mb-4 text-emerald-400">1. Select Target Tenant</h3>
                <div className="space-y-2 mb-6">
                  {[
                    { id: 'gymscribe', name: 'GymScribe Fit', desc: 'Fitness workspace database segment.' },
                    { id: 'bakeshop', name: 'BakeShop Pro', desc: 'Artisan retail bakery storefront logs.' },
                    { id: 'enterprisetech', name: 'Enterprise Tech', desc: 'High load corporate network platform.' }
                  ].map(t => (
                    <button
                      key={t.id}
                      onClick={() => setSelectedTenant(t.id)}
                      className={`w-full text-left p-3 rounded-xl border flex items-center gap-3 transition-all ${
                        selectedTenant === t.id ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-400 scale-[1.01]' : 'border-zinc-800/10 dark:border-zinc-700/10 hover:bg-zinc-800/5'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-500 shrink-0">
                        <FaUsers size={12} />
                      </div>
                      <div>
                        <h4 className="text-[10.5px] font-black uppercase text-white leading-none">{t.name}</h4>
                        <p className="text-[9px] text-zinc-450 mt-1">{t.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>

                <h3 className="text-xs font-black uppercase tracking-wider mb-4 text-emerald-400">2. Active Pricing Policy</h3>
                <div className="grid grid-cols-3 gap-2 mb-6">
                  {[
                    { id: 'dev', label: 'Testing Dev' },
                    { id: 'growth', label: 'Growth Plan' },
                    { id: 'enterprise', label: 'Enterprise' }
                  ].map(p => (
                    <button
                      key={p.id}
                      onClick={() => setPricingPlan(p.id)}
                      className={`py-2 rounded-lg text-[9px] font-black uppercase tracking-wider text-center transition-all ${
                        pricingPlan === p.id 
                          ? 'bg-emerald-500 text-white shadow font-black scale-102' 
                          : 'bg-zinc-900 border border-zinc-850 text-zinc-450'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>

                <h3 className="text-xs font-black uppercase tracking-wider mb-4 text-emerald-400">3. Connect Integration Outposts</h3>
                <div className="grid grid-cols-2 gap-2">
                  <button 
                    onClick={() => setStripeSync(!stripeSync)}
                    className={`py-2 px-3 border rounded-xl flex items-center justify-center gap-2 text-[9px] font-black uppercase transition-all ${
                      stripeSync ? 'bg-emerald-500/5 border-emerald-500/30 text-emerald-400' : 'border-zinc-800/15 text-zinc-500'
                    }`}
                  >
                    Stripe Checkout
                  </button>
                  <button 
                    onClick={() => setSendgridSync(!sendgridSync)}
                    className={`py-2 px-3 border rounded-xl flex items-center justify-center gap-2 text-[9px] font-black uppercase transition-all ${
                      sendgridSync ? 'bg-emerald-500/5 border-emerald-500/30 text-emerald-400' : 'border-zinc-800/15 text-zinc-500'
                    }`}
                  >
                    SendGrid Mail
                  </button>
                  <button 
                    onClick={() => setSlackSync(!slackSync)}
                    className={`py-2 px-3 border rounded-xl flex items-center justify-center gap-2 text-[9px] font-black uppercase transition-all ${
                      slackSync ? 'bg-emerald-500/5 border-emerald-500/30 text-emerald-400' : 'border-zinc-800/15 text-zinc-500'
                    }`}
                  >
                    Slack Alerts
                  </button>
                  <button 
                    onClick={() => setVercelSync(!vercelSync)}
                    className={`py-2 px-3 border rounded-xl flex items-center justify-center gap-2 text-[9px] font-black uppercase transition-all ${
                      vercelSync ? 'bg-emerald-500/5 border-emerald-500/30 text-emerald-400' : 'border-zinc-800/15 text-zinc-500'
                    }`}
                  >
                    Vercel Deploy
                  </button>
                </div>
              </div>

              <div className="mt-8">
                <button
                  onClick={handleCopySpec}
                  className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 text-white font-black text-xs tracking-wider transition-all"
                >
                  {copiedSpec ? <FaRegCheckCircle size={13} /> : <FaCopy size={12} />}
                  {copiedSpec ? "CONFIG SPEC SHEET COPIED!" : "COPY SPEC AND CONTACT US"}
                </button>
              </div>
            </div>

            {/* Right Screen Mock (7 columns) */}
            <div className="lg:col-span-7 flex flex-col justify-stretch">
              <div className="w-full h-full border rounded-3xl p-6 flex flex-col justify-between shadow-xl"
                   style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                
                <div>
                  <div className="flex items-center justify-between pb-3 border-b mb-6" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                      <span className="text-[10px] font-mono uppercase font-bold tracking-widest text-zinc-400">
                        Visual Multi-Tenant Dashboard Mockup
                      </span>
                    </div>
                    <span className="text-[9px] uppercase font-bold border px-2.5 py-0.5 rounded tracking-wide text-zinc-500" style={{ borderColor: 'var(--border-color)' }}>
                      Workspace Portal
                    </span>
                  </div>

                  {/* Browser Mock Wrapper */}
                  <div className="w-full border rounded-2xl overflow-hidden relative bg-zinc-950/45 border-zinc-850" style={{ backdropFilter: 'blur(10px)' }}>
                    
                    {/* URL Bar */}
                    <div className="bg-zinc-900 px-4 py-2 border-b border-zinc-850 flex items-center justify-between">
                      <div className="flex gap-1 items-center shrink-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                      </div>
                      
                      <div className="flex-grow max-w-sm mx-auto h-5 bg-zinc-950 rounded text-[9px] text-zinc-500 flex items-center justify-between px-2 font-mono border border-zinc-850">
                        <span>https://{selectedTenant}.my-saas-platform.com</span>
                        <span className="text-[8px] font-black text-emerald-400 uppercase">{pricingPlan}</span>
                      </div>

                      <div className="w-4" />
                    </div>

                    {/* Page Content Dashboard */}
                    <div className="p-4 space-y-3.5 text-left h-[260px] overflow-y-auto">
                      
                      {/* Dashboard Header */}
                      <div className="flex justify-between items-center pb-2 border-b border-zinc-850">
                        <div>
                          <span className="text-[6.5px] uppercase font-mono font-black text-emerald-400 tracking-widest leading-none block">ACTIVE ENVIRONMENT</span>
                          <h4 className="text-[11px] font-black text-white">{selectedTenant.toUpperCase()}_WORKSPACE</h4>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
                          <span className="text-[8px] font-mono text-zinc-400 uppercase tracking-wider">Sync Logged</span>
                        </div>
                      </div>

                      {/* Stat Metrics row */}
                      <div className="grid grid-cols-3 gap-2">
                        <div className="p-2 border rounded-xl bg-zinc-900/60 border-zinc-850 text-left">
                          <span className="text-[6px] uppercase tracking-wider text-zinc-500 font-bold block">Active Users</span>
                          <span className="text-[12px] font-mono font-black text-white">{activeUsersCount.toLocaleString()}</span>
                        </div>
                        <div className="p-2 border rounded-xl bg-zinc-900/60 border-zinc-850 text-left">
                          <span className="text-[6px] uppercase tracking-wider text-zinc-500 font-bold block">Req Flow / s</span>
                          <span className="text-[12px] font-mono font-black text-emerald-400">{incomingRequests} reqs</span>
                        </div>
                        <div className="p-2 border rounded-xl bg-zinc-900/60 border-zinc-850 text-left">
                          <span className="text-[6px] uppercase tracking-wider text-zinc-500 font-bold block">MRR Stream</span>
                          <span className="text-[12px] font-mono font-black text-purple-400">${mrrValue.toLocaleString()}</span>
                        </div>
                      </div>

                      {/* Live Webhook logs inspector */}
                      <div className="p-2.5 rounded-xl border border-zinc-850 bg-zinc-950 font-mono text-[7px]">
                        <span className="text-[6.5px] uppercase font-bold text-zinc-500 block mb-1.5 tracking-wider border-b border-zinc-850 pb-1">Real-time Webhook Interceptor Outpost</span>
                        <div className="space-y-1 h-[70px] overflow-y-auto text-zinc-400">
                          {webhookLogs.map((log, index) => (
                            <div key={index} className="truncate">
                              <span className="text-zinc-600 mr-1.5">➜</span>
                              <span className={log.includes('SYSTEM:') ? 'text-zinc-500' : 'text-emerald-400'}>{log}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                    </div>

                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── SIMULATED DEVELOPER JOURNEY CLI TIMELINE ── */}
      <section className="py-24 px-6 border-b" style={{ background: 'var(--bg-card)', borderColor: 'var(--section-divider)' }}>
        <div className="section-container max-w-4xl mx-auto">
          
          <div className="text-center mb-16">
            <p className="eyebrow mb-2">Development Timeline</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>Simulated SaaS Deployer</h2>
            <p className="subheadline max-w-2xl mx-auto">
              Follow our engineering pipeline stages. Click on a build stage below to run simulated terminal commands.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            
            {/* Timeline selector tabs (5 columns) */}
            <div className="md:col-span-5 space-y-3">
              {[
                { id: 'audit', step: '01', title: 'Security Audit', desc: 'Validate multi-tenant schema isolation layers.' },
                { id: 'routing', step: '02', title: 'URL subdomains Map', desc: 'Secure custom workspace routing headers.' },
                { id: 'billing', step: '03', title: 'Stripe Paywalls', desc: 'Connect payment webhooks and plans logic.' },
                { id: 'loadtesting', step: '04', title: 'Load Stress tests', desc: 'Test memory pools under heavy traffic spikes.' },
                { id: 'delivery', step: '05', title: 'Production release', desc: 'Launch active containers on VPC clusters.' }
              ].map(s => (
                <button
                  key={s.id}
                  onClick={() => {
                    setPipelineStage(s.id);
                    resetPipeline();
                  }}
                  className={`w-full text-left p-3.5 rounded-2xl border flex items-center gap-4 transition-all duration-200 ${
                    pipelineStage === s.id 
                      ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-400 scale-[1.01]' 
                      : 'border-zinc-800/10 dark:border-zinc-700/10 hover:bg-zinc-800/5'
                  }`}
                >
                  <span className={`text-md font-mono font-black ${pipelineStage === s.id ? 'text-emerald-400' : 'text-zinc-550'}`}>
                    {s.step}
                  </span>
                  <div>
                    <h4 className="text-xs font-black uppercase text-white leading-none mb-1">{s.title}</h4>
                    <p className="text-[9.5px] text-zinc-450">{s.desc}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Simulated terminal logs output (7 columns) */}
            <div className="md:col-span-7">
              <div className="w-full border rounded-2xl overflow-hidden bg-zinc-950 border-zinc-850 p-4 font-mono shadow-2xl relative">
                
                {/* File Header */}
                <div className="flex justify-between items-center pb-2.5 border-b border-zinc-850 mb-3.5 text-zinc-500 text-[10px]">
                  <span className="uppercase tracking-widest font-bold">Terminal : gcc_saas_sprint</span>
                  <div className="flex gap-1.5 shrink-0">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500/30" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/30" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/30" />
                  </div>
                </div>

                {/* Console Log Area */}
                <div className="min-h-[170px] text-left text-zinc-350 text-[10px] space-y-2 leading-relaxed">
                  {terminalLogs ? (
                    <pre className="whitespace-pre-wrap font-sans">{terminalLogs}</pre>
                  ) : (
                    <div className="text-zinc-500 flex flex-col items-center justify-center min-h-[160px] text-center">
                      <span className="text-2xl mb-2 text-zinc-650">🖧</span>
                      <span>Ready to trigger execution scripts for [stage: {pipelineStage.toUpperCase()}]</span>
                    </div>
                  )}
                </div>

                {/* Action Trigger */}
                <div className="mt-4 pt-3 border-t border-zinc-850 flex justify-between items-center gap-2">
                  <span className="text-[8px] text-zinc-500">Pipeline trigger: active</span>
                  <button
                    onClick={handleRunPipeline}
                    disabled={compilingCode || compiledSuccess}
                    className={`py-1.5 px-4 rounded-xl text-[9px] font-black uppercase transition-all tracking-wider ${
                      compiledSuccess 
                        ? 'bg-emerald-500 text-white font-black hover:opacity-90' 
                        : compilingCode 
                          ? 'bg-zinc-850 text-zinc-550 cursor-not-allowed'
                          : 'bg-zinc-800 text-white hover:bg-zinc-700 active:scale-95'
                    }`}
                  >
                    {compiledSuccess ? '✔ Deploy Completed' : compilingCode ? '⚡ Deploying...' : '▶ Deploy Stage'}
                  </button>
                </div>

              </div>
            </div>

          </div>

        </div>
      </section>

      {/* ── BOTTOM BACK CTA ── */}
      <section className="py-20 text-center" style={{ background: 'var(--bg-primary)' }}>
         <Link to="/services" className="text-sm font-semibold transition-colors inline-flex items-center gap-2"
               style={{ color: 'var(--accent)' }}>
            <FaArrowRight size={12} className="transform rotate-180 text-emerald-450" /> Back to All Services
         </Link>
      </section>

    </div>
  );
}
